// White label (PHASE24): the tenant's product name, colour, logo and sign-in message, and the web addresses
// it is reached at (its subdomain and its own domains, proven with a DNS TXT record, then given a certificate).
import * as React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Save, Upload, Trash2, Plus, RefreshCw, ShieldCheck } from 'lucide-react';
import { api, errorText } from '@/lib/api';
import { PageHeader, DataTable, ErrorBox, Field } from '@/components/common';
import { Button } from '@/components/ui/button';
import { Input, Textarea } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { StatusBadge, Badge } from '@/components/ui/badge';
import { toast } from '@/store/toast';
import { useCan } from '@/store/auth';
import { useSessionBranding, type Branding } from '@/lib/branding';
import { fmtTime } from '@/lib/utils';
import { t } from '@/i18n';

interface DomainRow {
  id: number; domain: string; status: string; txt_name: string; txt_value: string; verified_at: string | null; last_error: string | null;
  tls_status: string; tls_expires_at: string | null; tls_error: string | null;
}
interface BrandingInfo {
  settings: { product_name?: string | null; primary_color?: string | null; logo_file?: string | null; login_message?: string | null; support_email?: string | null };
  effective: Branding; default_product: string; subdomain: string | null; cname_target: string | null;
  certificates: 'automatic' | 'external'; logo_upload: boolean; domains: DomainRow[];
  app_icon_url: string | null;          // installed app icon (docs/PWA.md)
}

// The installed app's icon (2026-10-22, docs/PWA.md): the logo drawn in the middle of a white square, 192 and
// 512 pixels. The middle 70% holds the logo, so phones that cut icons into a circle keep all of it.
async function appIconsFromLogo(logoUrl: string): Promise<{ icon_192: string; icon_512: string }> {
  const img = new Image();
  img.src = logoUrl;
  await img.decode();
  const draw = (n: number) => {
    const c = document.createElement('canvas');
    c.width = n; c.height = n;
    const g = c.getContext('2d')!;
    g.fillStyle = '#ffffff';
    g.fillRect(0, 0, n, n);
    const box = n * 0.7;
    const k = Math.min(box / img.naturalWidth, box / img.naturalHeight);
    const w = img.naturalWidth * k, h = img.naturalHeight * k;
    g.imageSmoothingQuality = 'high';
    g.drawImage(img, (n - w) / 2, (n - h) / 2, w, h);
    return c.toDataURL('image/png');
  };
  return { icon_192: draw(192), icon_512: draw(512) };
}

export function BrandingPage() {
  const q = useQuery({ queryKey: ['branding'], queryFn: () => api<BrandingInfo>('/branding') });
  const canEdit = useCan('branding.edit');
  const qc = useQueryClient();
  const setSession = useSessionBranding((s) => s.set);
  // the new look applies to this portal at once
  const applied = (b: Branding) => { setSession(b); qc.invalidateQueries({ queryKey: ['public-branding'] }); q.refetch(); };
  const d = q.data;
  return (
    <div className="grid gap-4">
      <PageHeader title={t('Branding')} description={t('Your product name, colour and logo in the portal, and the web addresses your users sign in at.')} />
      <ErrorBox error={q.error} />
      {d && <>
        <div className="grid gap-4 xl:grid-cols-2">
          <LookCard info={d} canEdit={canEdit} onSaved={applied} />
          <LogoCard info={d} canEdit={canEdit} onSaved={applied} />
        </div>
        <DomainsCard info={d} canEdit={canEdit} onChange={() => q.refetch()} />
      </>}
    </div>
  );
}

function LookCard({ info, canEdit, onSaved }: { info: BrandingInfo; canEdit: boolean; onSaved: (b: Branding) => void }) {
  const s = info.settings;
  const [v, setV] = React.useState({ product_name: s.product_name ?? '', primary_color: s.primary_color ?? '', login_message: s.login_message ?? '',
                                     support_email: s.support_email ?? '' });
  const [error, setError] = React.useState<unknown>(null);
  const set = (k: keyof typeof v) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setV((x) => ({ ...x, [k]: e.target.value }));
  async function save(e: React.FormEvent) {
    e.preventDefault(); setError(null);
    try {
      const r = await api<{ effective: Branding }>('/branding', { method: 'PUT', body: { ...v, primary_color: v.primary_color || null } });
      toast.ok(t('Branding saved')); onSaved(r.effective);
    } catch (err) { setError(err); }
  }
  return (
    <Card>
      <CardHeader><CardTitle>{t('Look')}</CardTitle></CardHeader>
      <CardContent>
        <form onSubmit={save} className="grid gap-3">
          <Field help="branding.product_name" label={t('Product name')} hint={t('Shown in the menu, the sign-in page and the browser tab. Empty: "{{name}}".', { name: info.default_product })}>
            <Input name="product_name" value={v.product_name} onChange={set('product_name')} maxLength={80} disabled={!canEdit} placeholder={info.default_product} />
          </Field>
          <Field help="branding.primary_color" label={t('Main colour')} hint={t('Buttons and highlights. Empty: the default.')}>
            <div className="flex items-center gap-2">
              <input type="color" aria-label={t('Pick the main colour')} value={v.primary_color || '#171717'} disabled={!canEdit}
                onChange={(e) => setV((x) => ({ ...x, primary_color: e.target.value }))} className="h-9 w-12 cursor-pointer rounded border" />
              <Input name="primary_color" value={v.primary_color} onChange={set('primary_color')} placeholder="#1d4ed8" pattern="#[0-9a-fA-F]{6}" className="w-32" disabled={!canEdit} />
              {v.primary_color && canEdit && <Button type="button" variant="ghost" size="sm" onClick={() => setV((x) => ({ ...x, primary_color: '' }))}>{t('Default')}</Button>}
              <span className="ml-auto rounded px-3 py-1.5 text-sm" style={v.primary_color ? { background: v.primary_color, color: '#fff' } : undefined}>{t('Preview')}</span>
            </div>
          </Field>
          <Field help="branding.login_message" label={t('Sign-in message')} hint={t('Under the product name on the sign-in page.')}>
            <Textarea name="login_message" value={v.login_message} onChange={set('login_message')} maxLength={500} rows={2} disabled={!canEdit} />
          </Field>
          <Field help="branding.support_email" label={t('Support email')} hint={t('Shown on the sign-in page.')}>
            <Input name="support_email" type="email" value={v.support_email} onChange={set('support_email')} maxLength={128} disabled={!canEdit} />
          </Field>
          <ErrorBox error={error} />
          {canEdit && <div className="flex justify-end"><Button type="submit"><Save /> {t('Save')}</Button></div>}
        </form>
      </CardContent>
    </Card>
  );
}

function LogoCard({ info, canEdit, onSaved }: { info: BrandingInfo; canEdit: boolean; onSaved: (b: Branding) => void }) {
  const [file, setFile] = React.useState<File | null>(null);
  const [error, setError] = React.useState<unknown>(null);
  async function upload() {
    if (!file) return;
    setError(null);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await api<{ effective: Branding }>('/branding/logo', { raw: fd });
      toast.ok(t('Logo uploaded')); setFile(null);
      if (r.effective.logo_url) await makeAppIcon(r.effective.logo_url);
      onSaved(r.effective);
    } catch (err) { setError(err); }
  }
  async function makeAppIcon(logoUrl: string) {
    try { await api('/branding/app-icons', { body: await appIconsFromLogo(logoUrl) }); toast.ok(t('App icon made from the logo')); }
    catch (err) { setError(err); }
  }
  async function remove() {
    try { const r = await api<{ effective: Branding }>('/branding/logo', { method: 'DELETE' }); toast.ok(t('Logo removed')); onSaved(r.effective); }
    catch (err) { toast.error(errorText(err)); }
  }
  return (
    <Card>
      <CardHeader><CardTitle>{t('Logo')}</CardTitle></CardHeader>
      <CardContent className="grid gap-3">
        <div className="flex h-24 items-center justify-center rounded-md border bg-muted/30">
          {info.effective.logo_url ? <img src={info.effective.logo_url} alt={t('Current logo')} className="max-h-20 max-w-[16rem] object-contain" data-testid="logo-preview" />
            : <span className="text-sm text-muted-foreground">{t('No logo: the default icon is shown.')}</span>}
        </div>
        {!info.logo_upload && <p className="text-sm text-muted-foreground">{t('Logo upload is off on this server (BRANDING_DIR is not set).')}</p>}
        {canEdit && info.logo_upload && (
          <div className="flex flex-wrap items-center gap-2">
            <Input type="file" accept="image/png,image/jpeg,image/webp" aria-label={t('Logo file')} className="max-w-xs" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
            <Button onClick={upload} disabled={!file}><Upload /> {t('Upload')}</Button>
            {info.effective.logo_url && <Button variant="outline" onClick={remove}><Trash2 /> {t('Remove')}</Button>}
          </div>
        )}
        <p className="text-xs text-muted-foreground">{t('PNG, JPEG or WebP, at most 512 KB. A wide logo about 40 pixels high fits the menu; it is also the browser tab icon.')}</p>
        <div className="flex flex-wrap items-center gap-3 border-t pt-3" data-testid="app-icon">
          {info.app_icon_url
            ? <img src={info.app_icon_url} alt={t('App icon')} className="h-14 w-14 rounded-xl border object-cover" data-testid="app-icon-preview" />
            : <img src="/icons/icon-192.png" alt={t('App icon')} className="h-14 w-14 rounded-xl border" />}
          <div className="grid flex-1 gap-1 text-sm">
            <span className="font-medium">{t('App icon')}</span>
            <span className="text-xs text-muted-foreground">{info.app_icon_url
              ? t('The icon of the installed app on phones and computers, made from your logo.')
              : t('The installed app shows the standard icon until a logo is uploaded.')}</span>
          </div>
          {canEdit && info.logo_upload && info.effective.logo_url && (
            <Button variant="outline" size="sm" onClick={async () => { await makeAppIcon(info.effective.logo_url!); onSaved(info.effective); }}><RefreshCw /> {t('Make again from the logo')}</Button>
          )}
        </div>
        <ErrorBox error={error} />
      </CardContent>
    </Card>
  );
}

function DomainsCard({ info, canEdit, onChange }: { info: BrandingInfo; canEdit: boolean; onChange: () => void }) {
  const [domain, setDomain] = React.useState('');
  const [error, setError] = React.useState<unknown>(null);
  async function add(e: React.FormEvent) {
    e.preventDefault(); setError(null);
    try { await api('/branding/domains', { body: { domain } }); setDomain(''); toast.ok(t('Domain added: create its DNS records, then Verify')); onChange(); }
    catch (err) { setError(err); }
  }
  async function verify(r: DomainRow) {
    try { await api(`/branding/domains/${r.id}/verify`, { method: 'POST' }); toast.ok(t('{{domain}} verified', { domain: r.domain })); }
    catch (err) { toast.error(`${r.domain}: ${errorText(err)}`); }
    onChange();
    setTimeout(onChange, 4000);      // the certificate is requested in the background
  }
  async function tls(r: DomainRow) {
    try { await api(`/branding/domains/${r.id}/tls`, { method: 'POST' }); toast.ok(t('Certificate requested')); setTimeout(onChange, 4000); }
    catch (err) { toast.error(errorText(err)); }
  }
  async function remove(r: DomainRow) {
    if (!confirm(t('Remove {{domain}}? Users can no longer sign in there.', { domain: r.domain }))) return;
    try { await api(`/branding/domains/${r.id}`, { method: 'DELETE' }); toast.ok(t('Removed')); onChange(); } catch (err) { toast.error(errorText(err)); }
  }
  return (
    <Card>
      <CardHeader><CardTitle>{t('Web addresses')}</CardTitle></CardHeader>
      <CardContent className="grid gap-3">
        {info.subdomain
          ? <p className="text-sm">{t('Your users can always sign in at')} <a className="font-medium underline" href={`https://${info.subdomain}`} data-testid="subdomain">https://{info.subdomain}</a>{t(', with no "@company" in the user name.')}</p>
          : <p className="text-sm text-muted-foreground">{t('This server has no platform domain for tenant subdomains (PLATFORM_DOMAIN).')}</p>}
        <p className="text-sm text-muted-foreground">
          {t('Your own domain: add it, then create two DNS records at your DNS provider: a')} <b>CNAME</b> {t('from the domain to')}
          {' '}<code>{info.cname_target ?? t('this server')}</code>{t(', and the')} <b>TXT</b> {t('record shown below, which proves the domain is yours.')}
          {' '}{info.certificates === 'automatic' ? t("Once verified, a Let's Encrypt certificate is requested automatically.") : t('Certificates for your domain are set up by the platform operator.')}
        </p>
        <DataTable rowKey={(r) => r.id} rows={info.domains} empty={t('No domains of your own yet.')} columns={[
          { key: 'domain', label: t('Domain'), render: (r) => <span className="font-medium">{r.domain}</span> },
          { key: 'status', label: t('Status'), render: (r) => <span className="grid gap-0.5"><StatusBadge value={r.status} />
            {r.last_error && r.status !== 'VERIFIED' && <span className="text-xs text-destructive">{r.last_error.replace(/_/g, ' ').toLowerCase()}</span>}</span> },
          { key: 'txt', label: t('TXT record'), render: (r) => <span className="grid text-xs"><code>{r.txt_name}</code><code className="break-all">{r.txt_value}</code></span> },
          { key: 'tls_status', label: t('Certificate'), render: (r) => <span className="grid gap-0.5">
            <Badge variant={r.tls_status === 'ISSUED' ? 'success' : r.tls_status === 'FAILED' ? 'destructive' : 'outline'}>{t(r.tls_status.toLowerCase())}</Badge>
            {r.tls_expires_at && <span className="text-xs text-muted-foreground">{t('until {{date}}', { date: fmtTime(r.tls_expires_at) })}</span>}
            {r.tls_error && r.tls_status === 'FAILED' && <span className="max-w-xs text-xs text-destructive">{r.tls_error}</span>}</span> }]}
          actions={canEdit ? (r) => <div className="flex justify-end gap-1">
            <Button size="sm" variant="outline" onClick={() => verify(r)} aria-label={t('Verify {{domain}}', { domain: r.domain })}><RefreshCw /> {t('Verify')}</Button>
            {r.status === 'VERIFIED' && info.certificates === 'automatic' && r.tls_status !== 'REQUESTED' &&
              <Button size="sm" variant="ghost" onClick={() => tls(r)} aria-label={t('Certificate for {{domain}}', { domain: r.domain })}><ShieldCheck /> {t('Certificate')}</Button>}
            <Button size="icon" variant="ghost" aria-label={t('Remove {{domain}}', { domain: r.domain })} onClick={() => remove(r)}><Trash2 className="text-destructive" /></Button>
          </div> : undefined} />
        {canEdit && <form onSubmit={add} className="flex flex-wrap items-center gap-2">
          <Input name="domain" value={domain} onChange={(e) => setDomain(e.target.value)} placeholder="portal.yourcompany.com" className="max-w-xs" required />
          <Button type="submit"><Plus /> {t('Add domain')}</Button>
        </form>}
        <ErrorBox error={error} />
      </CardContent>
    </Card>
  );
}
